import React, { useState, useRef, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  AppBar,
  Toolbar,
  Typography,
  Button,
  IconButton,
  InputBase,
  Box,
  Tooltip,
} from '@mui/material';
import { Search, Close, FileDownload } from '@mui/icons-material';
import { exportFeedbacksDataCSV } from './../../../actions/exportCSVActions';
import { fetchFeedbacks } from '../../../actions/feedbacksActions';
import FeedbacksTable from '../../FeedbacksTable/FeedbacksTable';
import MainContentContainer from "../../MainContentContainer/MainContentContainer";
import { DEFAULT_PAGE, DEFAULT_PER_PAGE, PARSE_INT_RADIX } from '../../../constants/pagination';

const FeedbacksPage = () => {
  const dispatch = useDispatch();
  const searchInputRef = useRef(null);
  const searchTimeoutRef = useRef(null);
  const [searchText, setSearchText] = useState("");
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const { feedbacks: { data = [], pagination = { total: 0, page: 1, per_page: DEFAULT_PER_PAGE } } } = useSelector(state => state.feedbacks);

  // Combined effect for initial load and search
  useEffect(() => {
    // Clear any existing timeout
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    // Set up the timeout for the API call
    searchTimeoutRef.current = setTimeout(() => {
      dispatch(fetchFeedbacks(
        1,
        pagination.per_page,
        searchText || undefined
      ));
    }, searchText ? 300 : 0); // Only debounce if there's search text

    // Cleanup function
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, [dispatch, searchText, pagination.per_page]);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'f' && e.shiftKey) {
        e.preventDefault();
        if (!isSearchOpen) {
          handleToggleSearch();
        }
      }
      if (e.key === 'Escape' && isSearchOpen) {
        e.preventDefault();
        handleToggleSearch();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isSearchOpen]);

  const handleSearchChange = (e) => {
    setSearchText(e.target.value);
  };

  const handleToggleSearch = () => {
    setIsSearchOpen((prev) => !prev);
    if (isSearchOpen) {
      setSearchText("");
    } else {
      setTimeout(() => {
        searchInputRef.current?.focus();
      }, 0);
    }
  };

  /**
   * Handle export with current search filter
   * Updated per Monday.com Task #10746389124:
   * - Export now respects the current search filter
   */
  const handleExport = () => {
    dispatch(exportFeedbacksDataCSV({ search: searchText || undefined }));
  };

  const handlePageChange = (event, newPage) => {
    // MUI TablePagination is 0-based, API is 1-based
    dispatch(fetchFeedbacks(newPage + 1, pagination.per_page, searchText));
  };

  const handleRowsPerPageChange = (event) => {
    const newPerPage = parseInt(event.target.value, PARSE_INT_RADIX);
    dispatch(fetchFeedbacks(1, newPerPage, searchText));
  };

  return (
    <MainContentContainer>
      <AppBar position="sticky">
        <Toolbar>
          {isSearchOpen ? (
            <Box display="flex" alignItems="center" flexGrow={1}>
              <InputBase
                inputRef={searchInputRef}
                value={searchText}
                onChange={handleSearchChange}
                sx={{
                  flexGrow: 1,
                  px: 2,
                  py: 1,
                  backgroundColor: "white",
                  borderRadius: 1,
                  '& input': {
                    color: 'text.primary',
                  }
                }}
                placeholder="Search by message, user, or session..."
              />
              <IconButton color="inherit" onClick={handleToggleSearch}>
                <Close />
              </IconButton>
            </Box>
          ) : (
            <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
              Session Feedbacks
            </Typography>
          )}
          <Tooltip title="Search (⌘F)">
            <IconButton color="inherit" onClick={handleToggleSearch}>
              <Search />
            </IconButton>
          </Tooltip>
          <Button
            color="inherit"
            onClick={handleExport}
            startIcon={<FileDownload />}
            sx={{
              '&:hover': {
                backgroundColor: 'rgba(255, 255, 255, 0.1)'
              }
            }}
          >
            Export CSV
          </Button>
        </Toolbar>
      </AppBar>
      <FeedbacksTable
        data={data}
        searchText={searchText}
        pagination={pagination}
        onPageChange={handlePageChange}
        onRowsPerPageChange={handleRowsPerPageChange}
      />
    </MainContentContainer>
  );
};

export default FeedbacksPage;
